import { z } from 'zod';

/**
 * Platform-infrastructure DTOs — the superadmin's view of the GenieACS stack,
 * the management-plane tunnels and the deployment itself. These describe the
 * server-side backend from the GenieACS deployment guide, distinct from the
 * per-org TR-069 profiles and the per-site gateways operators manage.
 */

const acsHealthSchema = z.object({
  ok: z.boolean(),
  latencyMs: z.number(),
  error: z.string().nullable(),
});

/** One GenieACS provisioning object (a provision or a preset), by name. */
const genieObjectSchema = z.object({
  name: z.string(),
  detail: z.string().nullable(),
});

export const genieacsStatusSchema = z.object({
  health: acsHealthSchema,
  nbiUrl: z.string(),
  uiUrl: z.string(),
  cwmpUrl: z.string(),
  provisions: z.array(genieObjectSchema),
  presets: z.array(genieObjectSchema),
  /** The stock trio the guide says must be present (§6). */
  stock: z.object({
    bootstrap: z.boolean(),
    inform: z.boolean(),
    default: z.boolean(),
  }),
  fileCount: z.number().int(),
  /** True only when every stock provision is present. */
  healthy: z.boolean(),
});
export type GenieacsStatus = z.infer<typeof genieacsStatusSchema>;

/** One WireGuard peer as `wg show dump` reports it. */
const wgPeerSchema = z.object({
  publicKey: z.string(),
  endpoint: z.string().nullable(),
  latestHandshake: z.number().int(), // epoch seconds, 0 = never
  rxBytes: z.number().int(),
  txBytes: z.number().int(),
});

export const tunnelStatusSchema = z.object({
  execAdapter: z.enum(['shell', 'dry-run']),
  wg: z.object({
    interface: z.string(),
    address: z.string(), // ACS end of the tunnel, e.g. 10.79.0.1
    pool: z.string(),
    serverPublicKey: z.string(),
    endpoint: z.string(),
    /** Live peers from `wg show`; empty when the interface is down or dry-run. */
    peers: z.array(wgPeerSchema),
    /** True when the interface's CIDR gives room for the whole pool (guide §3). */
    poolWideEnough: z.boolean().nullable(),
  }),
  ovpn: z.object({
    host: z.string(),
    port: z.number().int(),
    proto: z.enum(['udp', 'tcp']),
    subnet: z.string(),
    gateway: z.string(),
    /** Whether the CA/server PKI answered — null when it could not be checked. */
    pkiPresent: z.boolean().nullable(),
    connectedClients: z.number().int().nullable(),
  }),
});
export type TunnelStatus = z.infer<typeof tunnelStatusSchema>;

/** One production-readiness check from the deploy runbook. */
const deployCheckSchema = z.object({
  id: z.string(),
  label: z.string(),
  ok: z.boolean().nullable(), // null = could not determine (e.g. dry-run)
  detail: z.string(),
});

export const deployStatusSchema = z.object({
  version: z.string(),
  execAdapter: z.enum(['shell', 'dry-run']),
  migrations: z.object({
    applied: z.number().int(),
    latest: z.string().nullable(),
  }),
  checks: z.array(deployCheckSchema),
});
export type DeployStatus = z.infer<typeof deployStatusSchema>;

/**
 * One manageable piece of infrastructure, as the Infrastructure index lists it.
 *
 * The index answers "what is running and is it healthy" in one screen; each
 * entry links to a detail page that is both its status and its editor. `scope`
 * separates what belongs to the whole platform from what an organization owns,
 * so a superadmin can see both without conflating them.
 */
export const infraItemSchema = z.object({
  /** Stable key; the client maps it to the page that owns it. */
  id: z.enum([
    'genieacs',
    'wireguard',
    'openvpn',
    'tr069',
    'deployment',
    'backups',
    // The rest of the Infrastructure section, summarised on its landing page.
    'olts',
    'gateways',
    'topology',
    // Platform-level plumbing that had no row anywhere until now.
    'storage',
    'map',
    // "Are the nightly jobs actually running" — asked at 2am, and until now
    // answerable only by reading the server's log.
    'scheduler',
  ]),
  name: z.string(),
  description: z.string(),
  /**
   * `network` is the fleet this section manages, `instance` the platform's own
   * machinery, `org` what a tenant owns. Three groups on the index, so the
   * OLTs are not filed under the same heading as the deployment's migrations.
   */
  scope: z.enum(['instance', 'org', 'network']),
  /**
   * `off` is NOT a fault: an optional integration nobody has configured, or a
   * list nobody has added to yet. It is deliberately distinct from `unknown`,
   * which means the reading could not be taken at all — a platform with no
   * Google key is working exactly as designed, and must not read like one
   * whose storage probe just failed.
   */
  state: z.enum(['ok', 'warn', 'down', 'unknown', 'off']),
  /** One line of current status, e.g. "up · 12 ms" or "3 peers". */
  status: z.string(),
  /** Whether this deployment lets the item be edited from the app yet. */
  editable: z.boolean(),
});
export type InfraItem = z.infer<typeof infraItemSchema>;

export const infraIndexSchema = z.object({
  items: z.array(infraItemSchema),
  /** Organizations the caller may inspect infrastructure for (superadmin: all). */
  orgs: z.array(z.object({ id: z.string(), name: z.string(), slug: z.string() })),
});
export type InfraIndex = z.infer<typeof infraIndexSchema>;

// --- Where the Settings rows on this index actually land -----------------------

/**
 * The addressable Settings tabs.
 *
 * Shared, rather than private to the web app, because two rows on this very
 * index deep link into them: `storage` and `map` each name a tab, and a tab id
 * that drifted would turn a working link into a page that silently renders its
 * default instead. One vocabulary, checked at both ends.
 *
 * `overview` is first because it is the default, and the default is what a bare
 * `/settings` — every existing link, bookmark and redirect — must keep landing
 * on.
 */
export const SETTINGS_TABS = [
  'overview',
  'ntp',
  // Its own tab, and the reason this list exists: the Platform index's
  // "File storage" row had nowhere to point but the page as a whole.
  'storage',
  'map',
  // Where mail comes FROM. Its own tab for the same reason `map` has one: it
  // is a credential plus a handful of values, it is off until somebody fills
  // it in, and the page that proves it works has to be an address you can be
  // sent to ("open /settings?tab=email and press Send test").
  'email',
  'white-label',
  'status-page',
  'smartolt',
  // The SMS gateway: a third-party connection with credentials and a request
  // template, configured in the same place as the other third-party
  // connections rather than buried under a notification feature that does not
  // exist yet.
  'sms',
  // Who the ACS lets in. A tab rather than a card because turning it back off
  // is an emergency action, and an emergency action needs an address you can
  // reach without hunting.
  'acs-auth',
] as const;
export type SettingsTab = (typeof SETTINGS_TABS)[number];
export const DEFAULT_SETTINGS_TAB: SettingsTab = 'overview';

/**
 * How hard the ACS checks who is informing to it.
 *
 * GenieACS decides this with ONE global expression, `cwmp.auth`, evaluated per
 * request with that device's own context available — so a single expression can
 * still separate tenants. Absent, it accepts everything: `if (r == null) return
 * true` in its own `lib/cwmp.ts`, which is the state this platform shipped in.
 *
 *   open         no `cwmp.auth` at all. Any device, any credentials or none.
 *                Where every install starts, and where this returns to if the
 *                stricter modes ever lock the fleet out.
 *   credentials  the credential IS the tenant. Each org's inform username and
 *                password is accepted and nothing else is. Closes "anything
 *                gets in" without depending on inventory being right, and a
 *                brand-new ONU authenticates before anything has synced it.
 *   inventory    additionally, the device must resolve to an ONU this org
 *                actually has. Strictly better separation — a leaked credential
 *                does not admit a stranger — and strictly more fragile, because
 *                it depends on the ACS's idea of a serial matching the
 *                chassis's. On this fleet a ZTE F670L reports `ZXXPQN0R9E62576`
 *                to TR-069 while its OLT knows it as `ZXICCD54C047`, so every
 *                one of those is a device `inventory` mode would refuse.
 *
 * The ladder only ever tightens by choice, and every rung is reversible from
 * the settings page — the failure this guards against is an operator who has
 * locked out the very ONUs they would need in order to fix it.
 */
export const ACS_AUTH_MODES = ['open', 'credentials', 'inventory'] as const;
export type AcsAuthMode = (typeof ACS_AUTH_MODES)[number];

/**
 * Which tab a URL is asking for, resolved against the tabs this viewer can
 * actually see.
 *
 * Three things fall back to the default rather than rendering nothing: no
 * `?tab=` at all (a bare `/settings`), a tab id that does not exist (a stale
 * bookmark, or a typo), and a tab that exists but is hidden from this viewer by
 * a capability check. That last one is the one that would otherwise bite — a
 * link to `?tab=status-page` handed to somebody without `infra.manage` would
 * select a tab with no button and no body, and the page would look broken.
 */
export function resolveSettingsTab(
  raw: string | null | undefined,
  available: readonly SettingsTab[] = SETTINGS_TABS,
): SettingsTab {
  return resolveTab(raw, available, DEFAULT_SETTINGS_TAB);
}

/**
 * The rule itself, once, for every tabbed page that keeps its tab in the URL.
 *
 * Written generic the moment there was a second such page rather than copied:
 * two implementations of "which tab did this URL ask for" would eventually
 * disagree about the hidden-tab case, which is the only case that is not
 * obvious — and this codebase has already paid for one duplicated rule that
 * drifted (`ensureSpeedProfileOnOlt`, review finding 15).
 */
function resolveTab<T extends string>(
  raw: string | null | undefined,
  available: readonly T[],
  fallback: T,
): T {
  const found = available.find((t) => t === raw);
  if (found) return found;
  return available.includes(fallback) ? fallback : (available[0] ?? fallback);
}

/**
 * The API keys page, in tabs — because the page holds two unrelated kinds of
 * secret and always did.
 *
 * `simpleacs` is access INTO the platform: the `sak_` keys an integration or
 * an assistant authenticates with. `finance` is money moving OUT and in: the
 * payment gateway credentials the billing run pays through. `services` is the
 * rest of what the platform holds on somebody else's behalf — object storage,
 * the map provider.
 *
 * They were three stacked cards on one scroll, which read as one list of "keys"
 * and buried the distinction that matters most: an operator handing somebody
 * an API key and an operator editing the M-Pesa passkey are doing jobs with
 * very different blast radii, and the page should not make them look like
 * neighbours.
 */
export const API_KEY_TABS = ['simpleacs', 'finance', 'services'] as const;
export type ApiKeyTab = (typeof API_KEY_TABS)[number];
export const DEFAULT_API_KEY_TAB: ApiKeyTab = 'simpleacs';

/** Which keys tab a URL is asking for, against the tabs this viewer has. */
export function resolveApiKeyTab(
  raw: string | null | undefined,
  available: readonly ApiKeyTab[] = API_KEY_TABS,
): ApiKeyTab {
  return resolveTab(raw, available, DEFAULT_API_KEY_TAB);
}

/**
 * The canonical link to one Settings tab.
 *
 * The default tab is a bare `/settings` on purpose: it is the address that
 * already exists in the nav, in redirects and in people's bookmarks, and
 * spelling it `?tab=overview` would quietly create a second URL for one page.
 */
export function settingsTabPath(tab: SettingsTab): string {
  return tab === DEFAULT_SETTINGS_TAB ? '/settings' : `/settings?tab=${tab}`;
}

export const acsAuthModeSchema = z.enum(ACS_AUTH_MODES);

export const updateAcsAuthSchema = z.object({
  mode: acsAuthModeSchema,
});
export type UpdateAcsAuth = z.infer<typeof updateAcsAuthSchema>;

/**
 * The fleet-wide TR-069 inform interval, as GenieACS's `inform` provision
 * declares it. Floor of 60s: at fleet scale every halving doubles the ACS's
 * inform load, and below a minute the informs ARE the traffic. Ceiling of a
 * day: a CPE that reports less often than daily is unmanaged in practice.
 */
export const updateAcsInformSchema = z.object({
  seconds: z.number().int().min(60).max(86_400),
});
export type UpdateAcsInform = z.infer<typeof updateAcsInformSchema>;

export const acsInformStatusSchema = z.object({
  seconds: z.number().int(),
});
export type AcsInformStatus = z.infer<typeof acsInformStatusSchema>;

/**
 * What the settings tab shows: the mode, and enough of the truth to decide.
 *
 * `published` is read back from GenieACS itself rather than from the setting
 * that asked for it, because those are two different facts and the gap between
 * them is the dangerous one — a mode saved here but never written there means
 * the fleet is open while the screen says it is closed.
 */
export const acsAuthStatusSchema = z.object({
  mode: acsAuthModeSchema,
  /** The expression currently live in GenieACS, with passwords masked. Null = none. */
  published: z.string().nullable(),
  /** Whether `published` matches what `mode` would produce. */
  inSync: z.boolean(),
  /** Orgs whose credentials the ACS would accept, for the operator to check. */
  credentials: z.array(
    z.object({ orgId: z.string(), orgName: z.string(), username: z.string() }),
  ),
  /**
   * `inventory` needs an extension script inside the GenieACS container. False
   * means the mode exists but cannot be selected yet, and saying so is better
   * than offering a switch that silently does nothing.
   */
  inventoryAvailable: z.boolean(),
  /** How many TR-069 devices could not be matched to an ONU — refused by `inventory`. */
  unmatchedDevices: z.number().int(),
});
export type AcsAuthStatus = z.infer<typeof acsAuthStatusSchema>;
